import { useState } from 'react';
import { Timer, Calendar, BarChart3, FolderOpen, Settings as SettingsIcon, LogOut, Menu, X, Receipt } from 'lucide-react';
import TimerPage from './pages/TimerPage';
import TimesheetPage from './pages/TimesheetPage';
import ReportsPage from './pages/ReportsPage';
import ProjectsPage from './pages/ProjectsPage';
import ExpensesPage from './pages/ExpensesPage';
import AdminPage from './pages/AdminPage';

type Page = 'timer' | 'timesheet' | 'reports' | 'projects' | 'expenses' | 'admin';

interface AppProps {
  signOut: () => void;
  user: any;
}

export default function App({ signOut, user }: AppProps) {
  const [page, setPage] = useState<Page>('timer');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems: { id: Page; label: string; icon: any; roles?: string[] }[] = [
    { id: 'timer', label: 'Timer', icon: Timer },
    { id: 'timesheet', label: 'Timesheet', icon: Calendar },
    { id: 'expenses', label: 'Expenses', icon: Receipt },
    { id: 'reports', label: 'Reports', icon: BarChart3 },
    { id: 'projects', label: 'Projects', icon: FolderOpen },
    { id: 'admin', label: 'Admin', icon: SettingsIcon, roles: ['Admin', 'Manager'] },
  ];

  const userRole = user?.signInDetails?.loginId?.includes('admin') ? 'Admin' : 'Consultant';
  const visibleNav = navItems.filter((item) => !item.roles || item.roles.includes(userRole));

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className="hidden md:flex md:flex-col md:w-64 bg-white border-r border-gray-200">
        <div className="flex items-center justify-center h-16 border-b border-gray-200">
          <h1 className="text-xl font-bold text-indigo-600">TimeTrack</h1>
        </div>
        <nav className="flex-1 px-4 py-6 space-y-2">
          {visibleNav.map((item) => (
            <button
              key={item.id}
              onClick={() => setPage(item.id)}
              className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                page === item.id
                  ? 'bg-indigo-50 text-indigo-700'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <item.icon className="w-5 h-5 mr-3" />
              {item.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center mb-3">
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
              <span className="text-sm font-medium text-indigo-700">
                {user?.signInDetails?.loginId?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-700">
                {user?.signInDetails?.loginId || 'User'}
              </p>
            </div>
          </div>
          <button
            onClick={signOut}
            className="w-full flex items-center px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Mobile menu */}
      <div className="md:hidden fixed inset-0 z-50" style={{ display: mobileMenuOpen ? 'block' : 'none' }}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setMobileMenuOpen(false)} />
        <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-xl">
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
            <h1 className="text-xl font-bold text-indigo-600">TimeTrack</h1>
            <button onClick={() => setMobileMenuOpen(false)}>
              <X className="w-6 h-6 text-gray-400" />
            </button>
          </div>
          <nav className="px-4 py-6 space-y-2">
            {visibleNav.map((item) => (
              <button
                key={item.id}
                onClick={() => { setPage(item.id); setMobileMenuOpen(false); }}
                className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg ${
                  page === item.id ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <item.icon className="w-5 h-5 mr-3" />
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setMobileMenuOpen(true)}>
            <Menu className="w-6 h-6 text-gray-600" />
          </button>
          <h1 className="text-lg font-bold text-indigo-600">TimeTrack</h1>
          <div className="w-6" />
        </header>
        <main className="flex-1 p-4 md:p-8 overflow-auto">
          <div className="max-w-7xl mx-auto">
            {page === 'timer' && <TimerPage />}
            {page === 'timesheet' && <TimesheetPage />}
            {page === 'reports' && <ReportsPage />}
            {page === 'projects' && <ProjectsPage />}
            {page === 'expenses' && <ExpensesPage />}
            {page === 'admin' && <AdminPage />}
          </div>
        </main>
      </div>
    </div>
  );
}
